import { z } from 'zod'
import { Model } from './architecture'
import { ListOfUrl } from './ListOfUrl'
import { I18nText } from './I18nText'
import type { Languages } from '@s4l/i18n/types/Languages'
import type { GETBannerInformationResponseType } from '../marketing/infrastructure/HTTPMarketingRepository/schemas/banners/banners-json'

export const ImageUrlSetValidation = z.object({
  srcSet: z.instanceof(ListOfUrl, { message: '[ImageUrlSet] srcSet is required' }),
  alt: z.instanceof(I18nText, { message: '[ImageUrlSet] alt is required' }),
})

export class ImageUrlSet extends Model {
  static create({ srcSet, alt }: z.infer<typeof ImageUrlSetValidation>) {
    ImageUrlSetValidation.parse({ srcSet, alt })
    return new ImageUrlSet(srcSet, alt)
  }

  static fromDataset({
    image,
  }: {
    image: NonNullable<
      GETBannerInformationResponseType['banners'][number]['content']['all']
    >['image']
  }) {
    return ImageUrlSet.create({
      srcSet: ListOfUrl.fromJSON({ list: image?.srcSet ?? [] }),
      alt: I18nText.create({ value: image?.alt ?? '' }),
    })
  }

  static fromCurrentApiDataset({
    image,
  }: {
    image: {
      srcSet?: Record<string, string> | string[]
      alt?: string | Partial<Record<string, string>>
    }
  }) {
    let srcSetArray: string[] = []

    if (image?.srcSet) {
      if (Array.isArray(image.srcSet)) {
        srcSetArray = image.srcSet
      } else if (typeof image.srcSet === 'object') {
        srcSetArray = Object.values(image.srcSet)
      }
    }

    return ImageUrlSet.create({
      srcSet: ListOfUrl.fromJSON({ list: srcSetArray }),
      alt: I18nText.create({
        value: typeof image?.alt === 'string' ? image.alt : image?.alt?.default || '',
      }),
    })
  }

  static fromJSON(json: ReturnType<ImageUrlSet['toJSON']>) {
    return new ImageUrlSet(ListOfUrl.fromJSON(json.srcSet), I18nText.create({ value: json.alt }))
  }

  constructor(
    private readonly _srcSet: ListOfUrl,
    private readonly _alt: I18nText
  ) {
    super()
  }

  get srcSet() { return this._srcSet } // prettier-ignore

  get alt() { return this._alt } // prettier-ignore

  getTranslatedImage(language?: Languages) {
    return {
      srcSet: this.srcSet.toJSON().list,
      alt: this.alt.getText(language),
    }
  }

  toJSON() {
    return { srcSet: this.srcSet.toJSON(), alt: this.alt.value }
  }
}
